import { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, DatePicker, Form, Input, InputNumber, Modal, Select, Tag, Tooltip, message, theme } from 'antd';
import { DeleteOutlined, DownOutlined, EditOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { personName } from '@/components/team-assignments/model';
import { archiveTeamTask, getTeamTasks, markTeamTask, saveTeamTask } from '@/services/teamAssignments.service';

export const TEAM_TASKS_KEY = 'team-tasks';
const RECURRENCES = ['ONCE', 'MONTHLY', 'QUARTERLY', 'YEARLY'];
const fmtDay = (d) => (d ? dayjs(d).format('DD.MM.YYYY') : '');

/** Adds or changes one task; managers only. */
function TaskDialog({ task, people, companies, onClose }) {
  const { t } = useTranslation('teamAssignments');
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const recurrence = Form.useWatch('recurrence', form);

  useEffect(() => {
    if (task === null) return;
    form.resetFields();
    form.setFieldsValue(
      task?._id
        ? { ...task, dueDate: task.dueDate ? dayjs(task.dueDate) : null, companies: task.companies.map((c) => c._id) }
        : { recurrence: 'MONTHLY', helpers: [], companies: [] }
    );
  }, [task, form]);

  const save = useMutation({
    mutationFn: (v) =>
      saveTeamTask({
        _id: task?._id,
        ...v,
        dueDate: v.dueDate ? v.dueDate.format('YYYY-MM-DD') : null,
      }),
    onSuccess: () => {
      message.success(t('messages.saved'));
      queryClient.invalidateQueries({ queryKey: [TEAM_TASKS_KEY] });
      onClose();
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  const peopleOptions = people.filter((p) => !p.left).map((p) => ({ value: p._id, label: personName(p) }));

  return (
    <Modal
      open={task !== null}
      title={task?._id ? t('tasks.edit') : t('tasks.new')}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t('actions.save')}
      cancelText={t('actions.cancel')}
      confirmLoading={save.isPending}
      okButtonProps={{ shape: 'round' }}
      cancelButtonProps={{ shape: 'round' }}
    >
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Form.Item name="title" label={t('tasks.fields.title')} rules={[{ required: true, whitespace: true, message: t('tasks.required') }]}>
          <Input maxLength={200} placeholder={t('tasks.fields.titlePlaceholder')} />
        </Form.Item>
        <div className="grid grid-cols-2 gap-x-3">
          <Form.Item name="lead" label={t('tasks.fields.lead')} rules={[{ required: true, message: t('tasks.required') }]}>
            <Select showSearch optionFilterProp="label" options={peopleOptions} />
          </Form.Item>
          <Form.Item name="helpers" label={t('tasks.fields.helpers')}>
            <Select mode="multiple" showSearch optionFilterProp="label" options={peopleOptions} />
          </Form.Item>
          <Form.Item name="recurrence" label={t('tasks.fields.recurrence')}>
            <Select options={RECURRENCES.map((r) => ({ value: r, label: t(`tasks.recurrence.${r}`) }))} />
          </Form.Item>
          {recurrence === 'ONCE' ? (
            <Form.Item name="dueDate" label={t('tasks.fields.dueDate')}>
              <DatePicker className="w-full" format="DD.MM.YYYY" />
            </Form.Item>
          ) : (
            <div className="grid grid-cols-2 gap-x-2">
              <Form.Item name="dueDay" label={t('tasks.fields.dueDay')}>
                <InputNumber className="!w-full" min={1} max={31} precision={0} />
              </Form.Item>
              {recurrence === 'QUARTERLY' && (
                <Form.Item name="dueMonth" label={t('tasks.fields.dueMonthOfQuarter')}>
                  <Select allowClear options={[1, 2, 3].map((m) => ({ value: m, label: t('tasks.monthOfQuarter', { n: m }) }))} />
                </Form.Item>
              )}
              {recurrence === 'YEARLY' && (
                <Form.Item
                  name="dueMonth"
                  label={t('tasks.fields.dueMonth')}
                  dependencies={['dueDay']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator: (_, value) =>
                        getFieldValue('dueDay') && !value ? Promise.reject(new Error(t('tasks.monthRequired'))) : Promise.resolve(),
                    }),
                  ]}
                >
                  <Select
                    allowClear
                    options={Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: dayjs().month(i).format('MMMM') }))}
                  />
                </Form.Item>
              )}
            </div>
          )}
        </div>
        <Form.Item name="companies" label={t('tasks.fields.companies')} extra={t('tasks.fields.companiesHint')}>
          <Select mode="multiple" showSearch optionFilterProp="label" options={companies.map((c) => ({ value: c._id, label: c.name }))} />
        </Form.Item>
        <Form.Item name="note" label={t('tasks.fields.note')}>
          <Input.TextArea maxLength={1000} autoSize={{ minRows: 2, maxRows: 5 }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/**
 * General admin tasks of the team (PEP and CROSO checks, beneficial owners,
 * the AML document…): not one client's work, or several clients' at once.
 * Each is ticked off for the period it is in; a monthly task opens again the
 * next month. Shown on the "Admin tasks" view and in one person's view.
 *
 * @param {Object} props
 * @param {Array} props.people - The team (board people).
 * @param {Array} props.companies - Every company on the board, for the picker.
 * @param {string} [props.person] - Only this person's tasks (lead or helper).
 * @param {boolean} props.isManager
 * @param {boolean} [props.hideWhenEmpty] - In one person's view: no list when there is nothing to show.
 */
export default function GeneralTasks({ people, companies, person, isManager, hideWhenEmpty = false }) {
  const { t } = useTranslation('teamAssignments');
  const { token } = theme.useToken();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(null);
  const { data } = useQuery({ queryKey: [TEAM_TASKS_KEY], queryFn: getTeamTasks });

  const tasks = useMemo(
    () => (data?.tasks || []).filter((x) => !person || x.lead === person || x.helpers.includes(person)),
    [data, person]
  );
  const byId = useMemo(() => new Map(people.map((p) => [p._id, p])), [people]);
  // Whoever has no board has no team list: the task brings the names on it.
  const nameOn = (id, name) => (byId.has(id) ? personName(byId.get(id)) : name || personName(null));

  const refresh = () => queryClient.invalidateQueries({ queryKey: [TEAM_TASKS_KEY] });
  const mark = useMutation({
    mutationFn: markTeamTask,
    onSuccess: refresh,
    // Also after a refusal: the period on turn may have moved since the list was read.
    onError: (e) => {
      message.error(e?.response?.data?.message || e.message);
      refresh();
    },
  });
  const archive = useMutation({
    mutationFn: archiveTeamTask,
    onSuccess: () => {
      message.success(t('messages.saved'));
      refresh();
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  if (!data || (hideWhenEmpty && !tasks.length && !isManager)) return null;
  // A manager keeps the line with "New task" here, but an empty list takes no room from the table.
  const hasList = tasks.length > 0 || !hideWhenEmpty;

  const dueText = (x) => {
    const when = x.recurrence === 'ONCE' ? fmtDay(x.dueDate) : x.dueAt ? fmtDay(x.dueAt) : '';
    return [t(`tasks.recurrence.${x.recurrence}`), when && t('tasks.dueBy', { date: when })].filter(Boolean).join(' · ');
  };
  const status = (x) => {
    if (x.done) {
      return (
        <Tooltip title={t('tasks.doneBy', { name: x.done.doneBy, date: fmtDay(x.done.doneAt) })}>
          <Tag color="success" className="!m-0 !rounded-full">{t('tasks.done')}</Tag>
        </Tooltip>
      );
    }
    if (x.dueAt && dayjs().isAfter(dayjs(x.dueAt), 'day')) {
      return <Tag color="error" className="!m-0 !rounded-full">{t('tasks.overdue')}</Tag>;
    }
    return <Tag className="!m-0 !rounded-full">{t('tasks.open')}</Tag>;
  };

  return (
    <section className="rounded-xl border px-3 py-2" style={{ borderColor: token.colorBorderSecondary }}>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="text" size="small" icon={!hasList ? undefined : open ? <DownOutlined /> : <RightOutlined />} onClick={() => setOpen((v) => !v)} aria-expanded={hasList && open}>
          <b>{t('tasks.title')}</b>
          <span className="ml-1 tabular-nums opacity-60">{tasks.length}</span>
        </Button>
        <span className="text-xs opacity-60">{t('tasks.hint')}</span>
        {isManager && (
          <Button className="ml-auto" shape="round" size="small" icon={<PlusOutlined />} onClick={() => setEditing({})}>
            {t('tasks.new')}
          </Button>
        )}
      </div>

      {open && hasList && (
        <ul className="m-0 mt-1 grid list-none gap-0 p-0">
          {!tasks.length && <li className="px-2 py-1 text-sm opacity-60">{t('tasks.empty')}</li>}
          {tasks.map((x) => (
            <li key={x._id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t px-2 py-1.5 text-sm first:border-t-0" style={{ borderColor: token.colorBorderSecondary }}>
              <Checkbox
                checked={!!x.done}
                disabled={!x.canMark || mark.isPending}
                onChange={(e) => mark.mutate({ taskId: x._id, done: e.target.checked, period: x.period })}
                aria-label={t('tasks.markDone', { title: x.title })}
              />
              <span className="min-w-[180px] flex-1">
                <span className="font-semibold">{x.title}</span>
                {x.note ? <span className="ml-2 text-xs opacity-60">{x.note}</span> : null}
              </span>
              <span className="text-xs opacity-75">{dueText(x)}</span>
              <span className="text-xs">
                {nameOn(x.lead, x.leadName)}
                {x.helpers.length ? <span className="opacity-60">{` + ${x.helpers.map((h, i) => nameOn(h, x.helperNames?.[i])).join(', ')}`}</span> : null}
              </span>
              {x.companies.length ? (
                <Tooltip title={x.companies.map((c) => c.name).join(', ')}>
                  <Tag className="!m-0 !rounded-full">{t('tasks.clients', { count: x.companies.length })}</Tag>
                </Tooltip>
              ) : (
                <Tag className="!m-0 !rounded-full">{t('tasks.general')}</Tag>
              )}
              {status(x)}
              {x.canEdit && (
                <span className="flex gap-1">
                  <Tooltip title={t('tasks.edit')}>
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setEditing(x)} aria-label={t('tasks.edit')} />
                  </Tooltip>
                  <Tooltip title={t('tasks.archive')}>
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={t('tasks.archive')}
                      onClick={() =>
                        Modal.confirm({
                          title: t('tasks.archiveConfirm', { title: x.title }),
                          okText: t('tasks.archive'),
                          cancelText: t('actions.cancel'),
                          okButtonProps: { shape: 'round', danger: true },
                          cancelButtonProps: { shape: 'round' },
                          onOk: () => archive.mutateAsync(x._id),
                        })
                      }
                    />
                  </Tooltip>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {isManager && <TaskDialog task={editing} people={people} companies={companies} onClose={() => setEditing(null)} />}
    </section>
  );
}
